import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { DatePicker, FormRow as Row, FormSection as Section, TextArea, Toggle } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { dateFormSchema } from '../../schemas';
import { TrainingFormValues } from '../../types';
import { DateFormValues } from './types';
import { WizardFooter } from './WizardFooter';

const TAGS = CT.academics.training.wizard;

interface Props {
  currentPage: number;
  form: Partial<TrainingFormValues>;
  wizardNext: (values: Partial<TrainingFormValues>) => void;
  wizardPrev: (values?: Partial<TrainingFormValues>) => void;
  wizardSubmit: (values: Omit<TrainingFormValues, 'id'>) => void;
  closeWizard: () => void;
}

export const DatesForm = (props: Props) => {
  const { form } = props;

  const methods = useCustomForm<DateFormValues>({
    resolver: zodResolver(dateFormSchema),
    defaultValues: {
      startDate: form.startDate,
      endDate: form.endDate,
      completedFlag: form.completedFlag,
      completionDate: form.completionDate,
      explanationTx: form.explanationTx,
    },
  });
  const { watch, setValue, getValues } = methods;

  const completedCheck = watch('completedFlag');

  useEffect(() => {
    if (!completedCheck) {
      setValue('completionDate', null);
    } else if (completedCheck && getValues('endDate') && !getValues('completionDate')) {
      setValue('completionDate', getValues('endDate'));
    }
  }, [completedCheck]);

  return (
    <FormProvider {...methods}>
      <Section componentsPerLine={1}>
        <Row>
          <DatePicker name={'startDate'} cyTag={TAGS.startDate} label={'Start Date'} required />
        </Row>
        <Row>
          <DatePicker name={'endDate'} cyTag={TAGS.endDate} label={'End Date'} />
        </Row>
        <Row>
          <Toggle
            name={'completedFlag'}
            cyTag={TAGS.completedToggle}
            label={'Did you complete the training?'}
          />
        </Row>
        {completedCheck && (
          <Row>
            <DatePicker
              name={'completionDate'}
              cyTag={TAGS.completionDate}
              label={'Completion Date'}
              required
            />
          </Row>
        )}
        <Row>
          <TextArea
            name={'explanationTx'}
            cyTag={TAGS.explainTextBox}
            label={'Additional Information'}
            description={'Comments or Description'}
          />
        </Row>
        <WizardFooter {...props} />
      </Section>
    </FormProvider>
  );
};
